import {
  Cartesian3,
  Cartographic,
  Ellipsoid,
  Matrix4,
  Transforms,
} from 'cesium';

/**
 * A local East-North-Up (ENU) frame anchored at an origin. Track geometry is
 * built in flat local metres (X=East, Y=North, Z=Up) where "perpendicular to
 * track" and "rail level" are meaningful, then transformed to ECEF on emit.
 *
 * Valid for tile-scale extents (a few km); re-anchor per tile to bound the
 * planar-projection error.
 */
export class EnuFrame {
  readonly origin: Cartesian3;
  readonly toFixed: Matrix4;
  readonly toLocal: Matrix4;

  constructor(origin: Cartesian3) {
    this.origin = origin;
    this.toFixed = Transforms.eastNorthUpToFixedFrame(origin);
    this.toLocal = Matrix4.inverse(this.toFixed, new Matrix4());
  }

  static fromDegrees(lon: number, lat: number, height = 0): EnuFrame {
    return new EnuFrame(Cartesian3.fromDegrees(lon, lat, height));
  }

  /** Local metres (E,N,U) → ECEF Cartesian3. */
  localToFixed(local: Cartesian3, result = new Cartesian3()): Cartesian3 {
    return Matrix4.multiplyByPoint(this.toFixed, local, result);
  }

  /** ECEF Cartesian3 → local metres (E,N,U). */
  fixedToLocal(fixed: Cartesian3, result = new Cartesian3()): Cartesian3 {
    return Matrix4.multiplyByPoint(this.toLocal, fixed, result);
  }
}

/** Geodetic surface normal ("up") at an ECEF position. */
export function geodeticUp(fixed: Cartesian3, result = new Cartesian3()): Cartesian3 {
  return Ellipsoid.WGS84.geodeticSurfaceNormal(fixed, result) ?? Cartesian3.UNIT_Z;
}

/** Convenience: lon/lat/height arrays → Cartographic[] for terrain sampling. */
export function toCartographics(
  coords: ReadonlyArray<readonly [number, number]>,
): Cartographic[] {
  return coords.map(([lon, lat]) => Cartographic.fromDegrees(lon, lat));
}
